import Layout from 'components/layout';
import Sections from 'components/sections';
import { getArticleSlugs } from 'lib/storyblok/getArticleSlugs';
import { getBlogPageData } from 'lib/storyblok/getBlogPageData';
import { getCategorySlugs } from 'lib/storyblok/getCategorySlugs';
import { getGlobalData } from 'lib/storyblok/getGlobalData';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { Storyblok } from 'types';

export default function DynamicPage({ globalData, pageData }) {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  /* Retrieve structured data */
  const structuredData = pageData?.content?.body?.[0]?.structuredData?.[0] ?? null;
  const structuredDataFaq =
    pageData?.content?.body?.[0]?.content?.content?.find(
      (e) => e?.type === 'blok' && e?.attrs?.body?.[0]?.component === 'articleFaq',
    )?.attrs?.body?.[0]?.faq ?? [];

  return (
    <Layout
      {...globalData}
      {...pageData.content}
      structuredData={[
        {
          type: 'article',
          data: {
            images: structuredData?.images?.map((e: Storyblok.Image) => e.filename) ?? [],
            datePublished: pageData.content.body[0].date,
            authorName: pageData.content.body[0].author?.name,
          },
        },
        ...(structuredDataFaq.length
          ? [
              {
                type: 'faq',
                data: {
                  mainEntity: structuredDataFaq.map((e) => ({
                    questionName: e.question,
                    acceptedAnswerText: e.answer,
                  })),
                },
              },
            ]
          : []),
      ]}
    >
      <Sections sections={pageData.content.body} />
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const articles = await getArticleSlugs();
  const categories = await getCategorySlugs();
  const paths = [
    ...articles.map((article) => ({
      params: { slug: article.slug.split('/') },
      locale: article.locale,
    })),
    ...categories.map((category) => ({
      params: { slug: [category.slug] },
      locale: category.locale,
    })),
  ];

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params: { slug }, preview = false, locale }) => {
  const { pageData } = await getBlogPageData(slug, {
    locale,
    preview,
  });

  if (!pageData) {
    return {
      notFound: true,
    };
  }

  const globalData = await getGlobalData({
    locale,
    preview,
  });

  return {
    props: {
      globalData,
      pageData,
    },
  };
};
